import { Controller } from 'react-hook-form';
import type { Control, FieldValues, Path, FieldError } from 'react-hook-form';
import type { SelectHTMLAttributes } from 'react';
import type { SelectOption } from '../inputs/Select';

interface FormSelectFieldProps<T extends FieldValues> extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'name'> {
  control: Control<T>;
  name: Path<T>;
  label: string;
  options: SelectOption[];
  error?: FieldError;
  hint?: string;
  required?: boolean;
  placeholder?: string;
  emptyText?: string;
  wrapperClassName?: string;
  selectClassName?: string;
  labelClassName?: string;
  valueAsNumber?: boolean;
}

export function FormSelectField<T extends FieldValues>({
  control,
  name,
  label,
  options,
  error,
  hint,
  required,
  placeholder,
  emptyText,
  wrapperClassName = 'mb-3',
  selectClassName = '',
  labelClassName = '',
  valueAsNumber = false,
  ...selectProps
}: FormSelectFieldProps<T>) {
  return (
    <div className={wrapperClassName}>
      <label className={`form-label ${required ? 'required' : ''} ${labelClassName}`}>{label}</label>
      <Controller
        control={control}
        name={name}
        render={({ field: { onChange, ...field } }) => (
          <select
            {...field}
            onChange={(e) => onChange(valueAsNumber ? Number(e.target.value) : e.target.value)}
            className={`form-select ${error ? 'is-invalid' : ''} ${selectClassName}`}
            disabled={options.length === 0 && !!emptyText}
            {...selectProps}>
            {options.length === 0 && emptyText ? (
              <option value="">{emptyText}</option>
            ) : (
              <>
                {placeholder && <option value="">{placeholder}</option>}
                {options.map((option) => (
                  <option key={option.value} value={option.value} disabled={option.disabled}>
                    {option.label}
                  </option>
                ))}
              </>
            )}
          </select>
        )}
      />
      {hint && <small className="form-hint">{hint}</small>}
      {error?.message && <div className="invalid-feedback d-block">{error.message}</div>}
    </div>
  );
}
